import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import RaidoField from "../../../components/common/RaidoField";
import ProductTable from "./ProductTable";
import NormalSearch from "../../../components/common/NormalSearch";
import {
  IconPackage,
  IconRefresh,
  IconScan,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { useWarehouseListData } from "../../../apis/warehouseApi";
import { Control, UseFormClearErrors } from "react-hook-form";
import { useFetchWarehouseRoutesData } from "../../../apis/routeApis";
import {
  useFetchDriversAllData,
  useFetchDriversByWarehouse,
  useFetchRouteDriversData,
} from "../../../apis/driverApis";
import {
  useFetchRouteOrdersData,
  useFetchOrderDetailsData,
} from "../../../apis/orderApis";
import { BrowserMultiFormatReader, NotFoundException } from "@zxing/library";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";
import { useLanguage } from "../../../context/LanguageContext";

// ---- Audio feedback helper ----
const playBeep = () => {
  const Ctx =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Ctx) return;
  const context = new Ctx();
  const oscillator = context.createOscillator();
  const gainNode = context.createGain();
  oscillator.connect(gainNode);
  gainNode.connect(context.destination);
  oscillator.frequency.value = 440;
  gainNode.gain.setValueAtTime(0, context.currentTime);
  gainNode.gain.linearRampToValueAtTime(0.5, context.currentTime + 0.05);
  oscillator.start(context.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(
    0.00001,
    context.currentTime + 0.2,
  );
  oscillator.stop(context.currentTime + 0.2);
};

// ---- Props ----
type Props = {
  register: any;
  setValue: any;
  watch: any;
  errors: any;
  clearErrors?: UseFormClearErrors<any>;
  control: Control<any>;
  drsData?: any;
  mode: string;
  setMode: React.Dispatch<React.SetStateAction<string>>;
  onSubmit?: () => void;
  isPending?: boolean;
  onClose?: () => void;
};

const SectionHeader = ({
  icon: Icon,
  title,
  className = "border-b border-[#FBD037]/30 pb-2 mb-3",
}: {
  icon: any;
  title: string;
  className?: string;
}) => {
  const { t } = useLanguage();
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {t(title)}
      </p>
    </div>
  );
};

// ---- Component ----
const GenerateDRSSheet = ({
  register,
  errors,
  setValue,
  watch,
  clearErrors,
  drsData,
  mode,
  setMode,
  onSubmit,
  isPending,
  onClose,
}: Props) => {
  const { t } = useLanguage();
  // pagination + search
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");

  const [loadOrder, setLoadOrder] = useState(true);

  // Single source of truth for selected orders
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [textareaValue, setTextareaValue] = useState("");

  // scanning
  const [isScanning, setIsScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReader = useRef<BrowserMultiFormatReader | null>(null);
  const lastScanTime = useRef<number>(0);
  const [orderIdToFetch, setOrderIdToFetch] = useState<string | null>(null);

  // API hooks
  const { data: warehouses } = useWarehouseListData();
  const warehouseId = watch("warehouseId");
  const routeId = watch("routeId"); // optional
  const driverId = watch("driverId");
  const capacity = watch("capacity");

  const canProceed = useMemo(
    () => Boolean(warehouseId && driverId && String(capacity ?? "").length > 0),
    [warehouseId, driverId, capacity],
  );

  const { data: routes } = useFetchWarehouseRoutesData(warehouseId ?? "");
  const routeCode = useMemo(
    () =>
      routes?.find((r: any) => String(r?.id) === String(routeId))?.routeCode ??
      "",
    [routes, routeId],
  );

  // const { data: routeDrivers } = useFetchRouteDriversData(
  //   routeId ? routeCode : undefined
  // );
  // const { data: allDrivers } = useFetchDriversAllData("");

  // const driverOptions = routeId && routeDrivers?.length
  //   ? routeDrivers
  //   : allDrivers || [];

  const { data: routeDriversRaw, refetch: fetchRouteDrivers } =
    useFetchRouteDriversData(routeId ? routeCode : undefined);

  const { data: allDriversRaw } = useFetchDriversByWarehouse(warehouseId);

  // Normalize API response
  const routeDrivers = Array.isArray(routeDriversRaw)
    ? routeDriversRaw
    : routeDriversRaw?.drivers || [];

  const allDrivers = Array.isArray(allDriversRaw)
    ? allDriversRaw
    : allDriversRaw?.drivers || [];

  // Final source
  const driverOptions =
    routeId && routeDrivers.length ? routeDrivers : allDrivers;

  const { data: orders, refetch } = useFetchRouteOrdersData(
    canProceed ? pageNo : 0,
    canProceed ? searchFor : "",
    routeId ? routeCode : "",
    drsData ? "update" : "create",
    drsData ? drsData?.id : "",
    warehouseId ? warehouseId : "",
  );

  useEffect(() => {
    setValue("capacity", "50");
  }, [setValue]);

  useEffect(() => {
    if (orders?.orders?.length > 0) {
      setValue("codAmount", orders?.orders?.[0]?.totalCodAmount);
    }
  }, [orders, setValue]);

  useEffect(() => {
    if (canProceed) refetch();
  }, [canProceed, pageNo, searchFor, refetch]);

  useEffect(() => {
    setValue("selectedOrders", selectedOrders);
  }, [selectedOrders, setValue]);

  useEffect(() => {
    if (drsData) {
      setValue("warehouseId", drsData?.warehouseId);
      setValue("routeId", drsData?.routeId || "");
      setValue("driverId", drsData?.driverId);
      if (Array.isArray(drsData?.shipments)) {
        const shipments = drsData.shipments.map((x: any) => String(x));
        setSelectedOrders(shipments);
        setTextareaValue(shipments.join("\n"));
      }
    }
  }, [drsData, setValue]);

  useEffect(() => {
    if (routeId) {
      fetchRouteDrivers();
    }
  }, [routeId]);

  const { data: fetchedOrderDetails, isFetching: isFetchingDetails } =
    useFetchOrderDetailsData(orderIdToFetch ?? "");

  const [scannedOrderDetails, setScannedOrderDetails] = useState<any[]>([]);

  useEffect(() => {
    if (fetchedOrderDetails && orderIdToFetch) {
      setScannedOrderDetails((prev) => {
        if (
          prev.find(
            (order) => order.awbNumber === fetchedOrderDetails.awbNumber,
          )
        ) {
          return prev;
        }
        return [...prev, fetchedOrderDetails];
      });
      setOrderIdToFetch(null);
    }
  }, [fetchedOrderDetails, orderIdToFetch]);

  // Scanner controls
  const stopScanner = useCallback(() => {
    if (codeReader.current) {
      codeReader.current.reset();
    }
    setIsScanning(false);
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  }, []);

  const startScanner = useCallback(async () => {
    if (!canProceed) return;
    if (!videoRef.current) return;
    try {
      if (!codeReader.current)
        codeReader.current = new BrowserMultiFormatReader();
      setIsScanning(true);
      lastScanTime.current = 0;

      await codeReader.current.decodeFromVideoDevice(
        null,
        videoRef.current,
        (result, error) => {
          const now = Date.now();
          if (result && now - lastScanTime.current > 1500) {
            lastScanTime.current = now;
            const scannedId = result.getText().trim();

            setSelectedOrders((prev) => {
              const deduped = Array.from(new Set([...prev, scannedId]));
              setTextareaValue(deduped.join("\n"));

              if (prev.includes(scannedId)) {
                console.warn(`Item "${scannedId}" already scanned.`);
                return prev;
              }
              playBeep();
              setOrderIdToFetch(scannedId);
              return deduped;
            });
          }
          if (error && !(error instanceof NotFoundException)) {
            console.error("Scanning error:", error);
          }
        },
      );
    } catch (error) {
      console.error("Scanner failed:", error);
      alert("Scanner error. Check camera permissions.");
      stopScanner();
    }
  }, [canProceed, stopScanner]);

  const toggleScanner = () => {
    if (!canProceed) {
      alert("Please select Warehouse, Driver and set Capacity first.");
      return;
    }
    if (isScanning) stopScanner();
    else startScanner();
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  useEffect(() => {
    if (mode !== "scan" && isScanning) {
      stopScanner();
    }
  }, [mode, isScanning, stopScanner]);

  return (
    <div className="relative  rounded-3xl  border flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 ">
        <h2 className="text-xl font-semibold text-[#212121] w-full">
          Create DRS
        </h2>

        <div className="flex items-center gap-2 w-full">
          <NewSelectInput
            className="w-full"
            name="warehouseId"
            removeDiv={true}
            placeholder={t("Select Warehouse")}
            selectOptions={
              warehouses?.warehouses?.map((w: any) => ({
                id: w?.id,
                value: w?.id,
                text: w?.name,
              })) || []
            }
            register={register}
            clearErrors={clearErrors}
            validation={{ required: t("Warehouse is required") }}
            error={errors.warehouseId}
            disabled={!warehouses || warehouses?.warehouses?.length === 0}
            value={warehouseId}
            setValue={(val: string) => {
              setValue("warehouseId", val);
              setValue("routeId", "");
              setValue("driverId", "");
              setSelectedOrders([]);
            }}
          />
          {onSubmit && (
            <button
              onClick={onSubmit}
              disabled={!canProceed || selectedOrders.length === 0 || isPending}
              className="px-4 py-[12px] bg-[#FBD037] text-[#212121] text-xs rounded-lg disabled:opacity-50"
            >
              {isPending ? t("Generating...") : t("Generate")}
            </button>
          )}

          <button
            onClick={onClose}
            className="p-3 rounded-lg border border-[#D2D2D2]"
          >
            <IconX size={16} />
          </button>
        </div>
      </div>

      <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 m-4 transition-all duration-300 space-y-3">
        {/* <SectionHeader icon={IconPackage} title="DRS" /> */}

        <div className="grid grid-cols-8 gap-4">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
          <NewSelectInput
            className="col-span-3"
            label={t("Route Code")}
            name="routeId"
            removeDiv={true}
            placeholder={t("Select Route")}
            selectOptions={
              routes?.map((r: any) => ({
                id: r?.id,
                value: r?.id,
                text: r?.routeCode,
              })) || []
            }
            register={register}
            clearErrors={clearErrors}
            error={errors.routeId}
            disabled={!routes || routes?.length === 0}
            value={routeId}
            setValue={(val: string) => {
              setValue("routeId", val);
              setValue("driverId", "");
              setSelectedOrders([]);
            }}
          />
          <NewSelectInput
            className="col-span-3"
            label={t("Driver")}
            name="driverId"
            removeDiv={true}
            placeholder={t("Select Driver")}
            selectOptions={
              driverOptions?.map((d: any) => ({
                id: d?.id,
                value: d?.id,
                text: d?.name,
              })) || []
            }
            register={register}
            clearErrors={clearErrors}
            validation={{ required: t("Driver is required") }}
            error={errors.driverId}
            disabled={!driverOptions?.length}
            value={driverId}
            setValue={(val: string) => setValue("driverId", val)}
          />
          <NewinputField
            className="col-span-2"
            title={t("Capacity")}
            name="capacity"
            type="number"
            removeDiv={true}
            placeholder={t("Capacity")}
            register={register}
            value={capacity}
            error={errors.capacity}
            validation={{ required: t("Capacity is required") }}
            inputClassName="w-full rounded-xl py-2 px-4 outline-none bg-white border border-gray-200 text-[#1E1E1E] text-sm focus:border-yellow-500"
          />
        </div>
      </div>

      <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 m-4 transition-all duration-300 space-y-3">
        <div className="flex justify-between items-center gap-2 flex-wrap border-b border-[#FBD037]/30 pb-2 mb-3">
          <SectionHeader
            className=""
            icon={IconPackage}
            title={`${t("Shipments list")} (${selectedOrders.length}/10)`}
          />

          <button
            type="button"
            onClick={() => {
              if (loadOrder) {
                setMode("automatic");
              } else {
                setMode("scan");
              }
              setLoadOrder(!loadOrder);
            }}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#FBD037] hover:bg-yellow-400 text-slate-800 transition active:scale-95 shadow-sm"
          >
            {loadOrder ? (
              <>
                <IconRefresh stroke={1.5} size={14} />
                <span>{t("Load Order")}</span>
              </>
            ) : (
              <>
                <IconRefresh stroke={1.5} size={14} />
                <span>{t("Paste AWB")}</span>
              </>
            )}
          </button>

          {isFetchingDetails && (
            <span className="text-xs text-gray-500">{t("Fetching details...")}</span>
          )}
        </div>
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

        <div>
          {!canProceed ? (
            <div className="text-sm text-red-500 bg-red-50/50 border border-red-100 rounded-xl p-3.5 text-center font-medium">
              {t("please select warehouse, driver and set capacity before proceeding.")}
            </div>
          ) : (
            <div className="space-y-4 bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
              {mode === "automatic" && (
                <ProductTable
                  orders={orders?.orders}
                  selectedOrders={selectedOrders}
                  setSelectedOrders={setSelectedOrders}
                />
              )}

              {mode === "scan" && (
                <textarea
                  value={textareaValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTextareaValue(value);

                    const parsed = value
                      .split(/[\n,\t]+/)
                      .map((o) => o.trim())
                      .filter((o) => o.length > 0);
                    const deduped = Array.from(new Set(parsed));
                    setSelectedOrders(deduped);
                  }}
                  placeholder=""
                  // className="w-full border outline-none border-gray-200 rounded-xl p-3 text-sm resize-none bg-slate-50 focus:bg-white focus:border-yellow-400 transition-colors"
                  rows={4}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateDRSSheet;
